import React, { useEffect, useState } from 'react';
import '../styles/RecordViewerPanel.css';

/*
 * Ported VERBATIM from Orcanos QMS `frontend/src/RecordViewerPanel.jsx` (the repository viewer's
 * shared helpers) so a DMS item's properties look and read the same in both products. The data
 * comes from `GET /api/review/reviews/{id}/item-form` — the same `QW_Get_Item_Add_Edit` (Edit
 * mode) call and field shape as QMS's `/orcanos/proxy/item-form`. Keep the two in step.
 */


// The backend resolves each section ID to its Orcanos name (`Data.work_item_section`), so the
// key IS the title. A bare numeric key only survives if Orcanos omitted that section's entry —
// show a neutral heading then, never the ID.
const SECTION_ID_RE = /^\d+$/;
export function sectionLabel(key) {
  if (!key || key === 'General') return { label: 'General', rawId: null };
  if (SECTION_ID_RE.test(key)) return { label: 'Other details', rawId: null };
  return { label: key, rawId: null };
}

// backend/api.py orders `sections` by Orcanos' own section_order (falling back to 0 for tenants
// that only give a plain section string, in which case min web_order breaks the tie). Re-derive
// here rather than trust object key order — JS reorders integer-like string keys numerically
// regardless of insertion order, and these section ids are exactly that.
export function orderedSectionNames(sections) {
  return sections ? Object.keys(sections).sort((a, b) => {
    const aOrder = Math.min(...sections[a].map(f => f.section_order ?? 0));
    const bOrder = Math.min(...sections[b].map(f => f.section_order ?? 0));
    if (aOrder !== bOrder) return aOrder - bOrder;
    const aWeb = Math.min(...sections[a].map(f => f.web_order ?? 0));
    const bWeb = Math.min(...sections[b].map(f => f.web_order ?? 0));
    return aWeb - bWeb;
  }) : [];
}

function stripHtml(html) {
  if (typeof html !== 'string') return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

const NOT_SET_RE = /^not set$/i;
export function isEmptyValue(v) {
  return v === null || v === undefined || v === '' || NOT_SET_RE.test(String(v).trim());
}

// current_value is the raw Text QW_Get_Item_Add_Edit returns — for a picklist/combobox that's
// usually the option's code, not its label, so resolve it against the field's own options list
export function FieldValue({ field }) {
  const raw = field.current_value;
  // The literal `Description` field on CAPA/NCR/Complaint-style item types is Orcanos' own
  // print-layout template (merge-field HTML), never free text a reviewer wrote — dumping it
  // reads as garbage. See _resolve_qms_description_field in backend/api.py.
  if (field.ws_add_col_name === 'Description' && (field.type === 'html' || field.type === 'richEditor')) {
    return <span className="rvp-field-template">Print-layout template — not real content, not shown</span>;
  }
  if (isEmptyValue(raw)) {
    return <span className="rvp-field-empty">—</span>;
  }
  if (field.type === 'html' || field.type === 'richEditor') {
    const text = stripHtml(raw);
    if (text.length > 500) {
      return <span className="rvp-field-template">Long templated content ({text.length.toLocaleString()} chars) — not shown</span>;
    }
    return <span className="rvp-field-text">{text}</span>;
  }
  if (Array.isArray(field.options) && field.options.length) {
    const opt = field.options.find(o => String(o.code) === String(raw));
    if (opt) return <span className="rvp-field-text">{opt.label}</span>;
  }
  if (field.type === 'multiSelect' && typeof raw === 'string' && raw.includes(',')) {
    return (
      <span className="rvp-pill-row">
        {raw.split(',').map(v => v.trim()).filter(Boolean).map((v, i) => (
          <span key={i} className="rvp-chip">{v}</span>
        ))}
      </span>
    );
  }
  return <span className="rvp-field-text">{String(raw)}</span>;
}

/**
 * "Item details" — the live Orcanos item form, ported from QMS `DocumentViewerPanel.jsx`
 * (lines 249-261 and 426-469): fields with a value or marked mandatory, a toggle for the rest.
 */
export function ItemDetails({ load, orcanosUrl }) {
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [url, setUrl] = useState(orcanosUrl || null);
  const [showEmptyFields, setShowEmptyFields] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    load()
      .then((d) => { if (!cancelled) { setSections(d.sections || {}); if (d.url) setUrl(d.url); } })
      .catch(() => { if (!cancelled) { setSections(null); setError('Could not load the live item form from Orcanos.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const sectionNames = orderedSectionNames(sections);
  const visibleSections = sectionNames.map((name) => {
    const fields = sections[name] || [];
    const kept = showEmptyFields ? fields : fields.filter((f) => !isEmptyValue(f.current_value) || f.is_mandatory);
    return { name, fields: kept, total: fields.length };
  }).filter((s) => s.fields.length > 0);
  const hiddenEmptyCount = sectionNames.reduce((s, n) => s + (sections[n]?.length || 0), 0)
    - visibleSections.reduce((s, x) => s + x.fields.length, 0);

  return (
    <div className="rvp-body" style={{ padding: 0 }}>
      {url && (
        <a className="rvp-open-orcanos" style={{ alignSelf: 'flex-start' }} href={url} target="_blank" rel="noopener noreferrer">
          Open in Orcanos ↗
        </a>
      )}
      {loading && <div className="rvp-loading">Loading item form from Orcanos…</div>}
      {error && <div className="rvp-error-banner">{error}</div>}
      {!loading && !error && visibleSections.length > 0 && (
        <>
          {hiddenEmptyCount > 0 && (
            <button type="button" className="rvp-toggle-empty" onClick={() => setShowEmptyFields((v) => !v)}>
              {showEmptyFields ? 'Hide empty fields' : `Show ${hiddenEmptyCount} empty field${hiddenEmptyCount === 1 ? '' : 's'}`}
            </button>
          )}
          {visibleSections.map(({ name, fields, total }) => {
            const { label } = sectionLabel(name);
            return (
              <div className="cp-edit-section" key={name}>
                <div className="cp-edit-section-title">
                  <span>{label}</span>
                  <span className="cp-edit-section-count">{fields.length}{fields.length !== total ? ` / ${total}` : ''} field{total === 1 ? '' : 's'}</span>
                </div>
                <div className="cp-edit-section-body">
                  <div className="rvp-field-grid">
                    {fields.map((f) => (
                      <div className="rvp-field-row" key={f.name}>
                        <span className="rvp-field-label">{f.title.replace(/[:*]+\s*$/, '')}{f.is_mandatory && <span className="rvp-mandatory">*</span>}</span>
                        <FieldValue field={f} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
